import type { FC } from 'react';
import React, { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, useTheme } from '@mui/material';

import type {
  CodingResult,
  DashboardWidgetProps,
  GroupedCodingResult,
} from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import { useCodingResults } from 'src/components/dashboard/DashboardContext/CodingResultsContext';
import { groupCalculatedCodingResults } from 'src/components/dashboard/DashboardContext/CodingResultsContext/groupCalculatedCodingResult';
import DashboardCommentsProvider from 'src/components/dashboard/DashboardContext/CommentsContext';
import WidgetFrame from 'src/components/dashboard/DashboardPage/components/WidgetFrame';
import NoResultsToShowAdjustFiltersWarning from 'src/components/followUp/common/warning/NoResultsToShowAdjustFiltersWarning';
import type { WidgetFooterLegendLabel } from 'src/components/followUp/common/WidgetFooterLegend/WidgetFooterLegend';
import WidgetFooterLegend from 'src/components/followUp/common/WidgetFooterLegend/WidgetFooterLegend';
import WidgetHeaderWithPill from 'src/components/followUp/common/WidgetHeaderWithPill/WidgetHeaderWithPill';
import useGenerateWidgetEndpoint from 'src/components/followUp/hooks/useGenerateWidgetEndpoint';
import useWidgetQuery from 'src/components/followUp/hooks/useWidgetQuery';

import { applySorting, filterSentimental, filterTopic } from '../helper';
import type { OpenCommentFilter } from '../types';

import HRQSCommentsWidgetContent from './HRQSCommentsWidgetContent';
import { useExportOptions } from './useHRQSOpenCommentsExportOptions';

const initialFilter: OpenCommentFilter = {
  topicFilter: 'all',
  sentimentFilter: 'totalOfAllCounts',
  sorting: 'frequency',
};

const groupResultsWithComparison = (
  results: GroupedCodingResult[],
  comparisonData?: CodingResult[],
) => {
  if (!comparisonData) return results;
  const groupedComparisonData = groupCalculatedCodingResults(comparisonData);
  return results.map((topic) => {
    const comp: GroupedCodingResult | undefined = groupedComparisonData.find(
      (comparisonDate) => comparisonDate.label === topic.label,
    );

    return {
      ...topic,
      comparison: comp,
      subTopics: topic.subTopics?.map((sub) => ({
        ...sub,
        comparison: comp?.subTopics?.find(
          (comparisonDate) => comparisonDate.label === sub.label,
        ),
      })),
    };
  });
};

const HRQSOpenCommentsWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<OpenCommentFilter>(initialFilter);
  const ariaId = useId();
  const { colors } = useTheme();
  const endpoint = useGenerateWidgetEndpoint(
    apiPaths.WIDGET_DATA.OPEN_COMMENTS.TOPICS,
    config.id,
  );
  const { debouncedTextFilter } = useCodingResults();

  const { data, refetch, isFetching, errorMsg, comparisonData, queryFilters } =
    useWidgetQuery<CodingResult[]>(endpoint, {
      extraFilters: { textFilter: debouncedTextFilter },
    });
  const labels: WidgetFooterLegendLabel[] = useMemo(
    () => [
      {
        color: colors.error[600],
        comparisonColor: comparisonData && colors.comparison[400],
        name: t('followUp:statistics.openCommentsWidget.labels.negative'),
      },
      {
        color: colors.neutral[500],
        comparisonColor: comparisonData && colors.comparison[200],
        name: t('followUp:statistics.openCommentsWidget.labels.neutral'),
      },
      {
        color: colors.tertiary[600],
        comparisonColor: comparisonData && colors.comparison[100],
        name: t('followUp:statistics.openCommentsWidget.labels.positive'),
      },
    ],
    [colors, comparisonData, t],
  );
  const sortedResults = useMemo(() => {
    let result = groupCalculatedCodingResults(data ?? []);
    result = filterSentimental(result, filter.sentimentFilter);
    result = filterTopic(result, filter.topicFilter);
    return applySorting(result, 'frequency', filter.sentimentFilter, 30);
  }, [data, filter]);
  const resultsWithComparison = useMemo(
    () => groupResultsWithComparison(sortedResults, comparisonData),
    [sortedResults, comparisonData],
  );

  const highestTotal = useMemo(
    () => sortedResults?.[0]?.totalOfAllCounts ?? 0,
    [sortedResults],
  );

  const exportOptions = useExportOptions({
    data,
    comparisonData,
    widgetName: config.name,
    queryFilters,
  });

  return (
    <WidgetFrame
      ariaId={ariaId}
      refetch={refetch}
      loading={isFetching}
      error={errorMsg}
      minHeight="auto"
      maxHeight="auto"
      header={<WidgetHeaderWithPill title={config.name} ariaId={ariaId} />}
      footer={
        highestTotal === 0 ? (
          <NoResultsToShowAdjustFiltersWarning />
        ) : (
          <WidgetFooterLegend labels={labels} />
        )
      }
      exportOptions={exportOptions}
    >
      <Stack
        sx={{
          height: '100%',
          width: '100%',
          px: 3,
          gap: 2,
        }}
      >
        <DashboardCommentsProvider source="hrqs" widgetId={config.id}>
          <HRQSCommentsWidgetContent
            filter={filter}
            resultsWithComparison={resultsWithComparison}
            setFilter={setFilter}
            highestTotal={highestTotal}
            showTranslationToggle={config.show_translation_toggle ?? true}
          />
        </DashboardCommentsProvider>
      </Stack>
    </WidgetFrame>
  );
};
export default HRQSOpenCommentsWidget;
